import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/core/api/apiClient';
import type {
  UserPreferences,
  UserDashboard,
  StarredItemCreateRequest,
  ReorderStarredItemsRequest,
  DashboardPreferencesUpdateRequest,
} from '@/shared/types/api';

export function useUserPreferences() {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<UserPreferences>('/users/me/preferences');
      setPreferences(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  return { preferences, loading, error, refetch: fetchPreferences };
}

export function useStarredItemMutations() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addStarredItem = useCallback(async (data: StarredItemCreateRequest): Promise<UserPreferences> => {
    setSaving(true);
    setError(null);
    try {
      return await apiClient.post<UserPreferences>('/users/me/starred', data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to star item';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const removeStarredItem = useCallback(async (itemId: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.delete(`/users/me/starred/${itemId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to unstar item';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const reorderStarredItems = useCallback(async (data: ReorderStarredItemsRequest): Promise<UserPreferences> => {
    setSaving(true);
    setError(null);
    try {
      return await apiClient.put<UserPreferences>('/users/me/starred/reorder', data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reorder items';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateDashboardPreferences = useCallback(async (data: DashboardPreferencesUpdateRequest): Promise<UserPreferences> => {
    setSaving(true);
    setError(null);
    try {
      return await apiClient.put<UserPreferences>('/users/me/preferences/dashboard', data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update preferences';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    addStarredItem,
    removeStarredItem,
    reorderStarredItems,
    updateDashboardPreferences,
    saving,
    error,
    clearError: () => setError(null),
  };
}

export const DASHBOARD_REFRESH_EVENT = 'dashboard:refresh';

export function useDashboard() {
  const [dashboard, setDashboard] = useState<UserDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<UserDashboard>('/users/me/dashboard');
      setDashboard(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refetch when any component dispatches the refresh event
  useEffect(() => {
    const handler = () => { fetchDashboard(); };
    window.addEventListener(DASHBOARD_REFRESH_EVENT, handler);
    return () => window.removeEventListener(DASHBOARD_REFRESH_EVENT, handler);
  }, [fetchDashboard]);

  return { dashboard, loading, error, refetch: fetchDashboard };
}
